import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { CAR_SIZE, CAR_SLOT_MAPPING } from "../../utils/constants";
import {
  createColumn,
  createEditAction,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_CAR_SLOT_MAPPING } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_CAR_SIZE_SLOT_MAPPING, API_CONFIGURABLES } from "../../utils/apis";
import { TFormHookParams } from "../../types/common";

// Helper function to resolve car size name by ID
const resolveCarSizeName = (carSizeId: any, carSizesData: any) => {
  if (!carSizeId || !carSizesData) {
    return "Unknown Car Size";
  }

  // Handle both array and object with results property
  const carSizesArray = Array.isArray(carSizesData)
    ? carSizesData
    : carSizesData?.results || [];

  if (!Array.isArray(carSizesArray) || carSizesArray.length === 0) {
    return "Loading...";
  }

  const carSize = carSizesArray.find((cs) => cs.id === carSizeId);

  return carSize ? carSize.name : "Unknown Car Size";
};

export const useCarSlotMapping = () => {
  // Get car sizes data for resolving names in table
  const {
    loadData: loadCarSizesData,
    loading: loadingCarSizes,
    data: carSizesData,
  } = useGet();

  useEffect(() => {
    loadCarSizesData({
      api: API_CONFIGURABLES,
      params: {
        key: CAR_SIZE,
      },
    });
  }, []);

  const tableColumns = () => [
    createColumn("car_size_name", true, "Car Size"),
    createColumn("number_of_slots", true, "Number of Slots"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        car_size_name: resolveCarSizeName(item.car_size, carSizesData),
        actions: (
          <FormActions
            actions={[createEditAction(URL_CAR_SLOT_MAPPING, item?.id)]}
          />
        ),
      };
    });
  };

  const formRoute = getForm(URL_CAR_SLOT_MAPPING);
  const activityFilter = "car_slot_mappings";

  return {
    tableData,
    tableColumns,
    formRoute,
    activityFilter,
    carSizesData,
    loadingCarSizes,
  };
};
export const useCarSlotMappingForm = ({
  updateFormValues,
}: TFormHookParams = {}) => {
  const {
    loadData: loadCarSizes,
    loading: loadingCarSizes,
    data: carSizesData,
  } = useGet();
  const { loadData } = useGet();

  useEffect(() => loadData({ api: API_CAR_SIZE_SLOT_MAPPING }), []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        street_type: CAR_SLOT_MAPPING,
      });
    }
  }, [updateFormValues]);

  useEffect(() => {
    loadCarSizes({
      api: API_CONFIGURABLES,
      params: {
        key: CAR_SIZE,
      },
    });
  }, []);

  const fields: any = [
    {
      field_name: "car_size",
      label: "Car Size",
      type: "select",
      props: { required: true },
      options_data: carSizesData?.results,
      loading: loadingCarSizes,
    },
    {
      field_name: "number_of_slots",
      label: "Number of Slots",
      type: "number",
      props: { required: true, min: 1 },
    },
  ];

  const sections: any = [
    {
      title: "Car Slot Mapping Details",
      fields,
    },
  ];

  const redirect = getRegister(URL_CAR_SLOT_MAPPING);
  const defaultValues = {};

  return { fields, sections, redirect, defaultValues };
};

export const CarSlotMappingForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { fields } = useCarSlotMappingForm();
  return (
    <FormFields
      fields={fields}
      error={error}
      form={form}
      updateFormValue={updateFormValue}
      onChange={onChange}
    />
  );
};
